import "./NewPayslip.css";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useContext } from "react";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  getEmployeePayStatements,
  getUsersSummary,
  getUserById,
} from "../../../api/api";
import Preloader from "../../Preloader/Preloader";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import briefcaseIcon from "../../../images/Briefcase.svg";
import bankIcon from "../../../images/Bank.svg";
import alarmIcon from "../../../images/Alarm.svg";
import catPhoto from "../../../images/cat-photo.png";
import { Line } from "react-chartjs-2";
import { Doughnut } from "react-chartjs-2";
import "../../../components/Chart/ChartConfig";
import "../../Table/Table.css";
import Select from "react-select";

registerLocale("ru", ru);

const checkUserAccess = (user, accessLevel) => {
  return user?.access_levels?.some((level) => level.name === accessLevel);
};

// Маппинг месяцев английский -> русский
const monthMapping = {
  Jan: "Янв",
  Feb: "Фев",
  Mar: "Мар",
  Apr: "Апр",
  May: "Май",
  Jun: "Июн",
  Jul: "Июл",
  Aug: "Авг",
  Sep: "Сен",
  Oct: "Окт",
  Nov: "Ноя",
  Dec: "Дек",
};

// Обратный маппинг русский -> английский
const monthMappingReverse = Object.fromEntries(
  Object.entries(monthMapping).map(([eng, rus]) => [rus, eng])
);

// Функция для конвертации английского названия месяца в русское
const convertMonthToRussian = (englishMonth) => {
  return monthMapping[englishMonth] || englishMonth;
};

// Функция для конвертации русского названия месяца в английский
const convertMonthToEnglish = (russianMonth) => {
  return monthMappingReverse[russianMonth] || russianMonth;
};

// Функция для конвертации даты "Jan-2025" в "Янв-2025"
const convertDateToRussian = (dateString) => {
  if (!dateString || typeof dateString !== "string") return dateString;
  const [month, year] = dateString.split("-");
  const russianMonth = convertMonthToRussian(month);
  return year ? `${russianMonth}-${year}` : russianMonth;
};

// Функция для конвертации даты "Янв-2025" в "Jan-2025"
const convertDateToEnglish = (dateString) => {
  if (!dateString || typeof dateString !== "string") return dateString;
  const [month, year] = dateString.split("-");
  const englishMonth = convertMonthToEnglish(month);
  return year ? `${englishMonth}-${year}` : englishMonth;
};

function NewPayslip({ forceShowSelector }) {
  const { userData } = useContext(UserContext);
  const isAdmin = checkUserAccess(userData, "АДМС");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedPeriod, setSelectedPeriod] = useState("year");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployeeFullData, setSelectedEmployeeFullData] =
    useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);

  const monthNamesRu = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ];

  const monthNamesRuNom = [
    "январь",
    "февраль",
    "март",
    "апрель",
    "май",
    "июнь",
    "июль",
    "август",
    "сентябрь",
    "октябрь",
    "ноябрь",
    "декабрь",
  ];

  const monthPickerRef = useRef(null);
  const handleMonthClick = () => {
    if (monthPickerRef.current) {
      monthPickerRef.current.setFocus();
    }
  };

  const MIN_YEAR = 2023;
  const MAX_YEAR = new Date().getFullYear();
  const MIN_DATE = new Date(2023, 0, 1); // 1 января 2023
  const MAX_DATE = new Date(); // сегодня

  useEffect(() => {
    const fetchPayrollData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Используем данные выбранного сотрудника или текущего пользователя
        const targetUser = selectedEmployee?.userData || userData;

        console.log("Fetching payroll data for user:", {
          selectedEmployee: selectedEmployee?.value,
          targetUser: targetUser?.id,
          targetUserData: targetUser,
        });

        if (!targetUser) {
          console.warn("Данные пользователя отсутствуют");
          setError("Данные пользователя не загружены");
          return;
        }

        // Получаем ФИО из данных пользователя (поддерживаем старую и новую структуру)
        const surname = targetUser.surname || targetUser.lastName || "";
        const name = targetUser.name || targetUser.firstName || "";
        const patronymic = targetUser.patronymic || targetUser.middleName || "";

        if (!surname || !name) {
          console.warn("Отсутствует фамилия или имя пользователя:", {
            surname,
            name,
            patronymic,
            targetUser,
          });
          setError("Не удалось получить ФИО пользователя");
          return;
        }

        // Формируем полное имя (отчество может отсутствовать)
        const fullName = patronymic
          ? `${surname} ${name} ${patronymic}`
          : `${surname} ${name}`;

        // Формируем даты за последний год
        const today = new Date();
        const dateEnd = today.toISOString().split("T")[0];
        const dateStart = new Date(today.setFullYear(today.getFullYear() - 1))
          .toISOString()
          .split("T")[0];

        const data = await getEmployeePayStatements(
          fullName,
          dateStart,
          dateEnd
        );

        if (!data || data?.message === "Данные не найдены") {
          console.warn("Данные не найдены для пользователя:", fullName);
          setPayrollData(null);
        } else {
          // Трансформируем данные в нужный формат
          const transformedData = transformPayrollData(data);
          setPayrollData(transformedData);
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных о выплатах:", error);
        setError(error.message);
        setPayrollData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayrollData();
  }, [userData, selectedEmployee?.value]);

  // Функция для трансформации данных от API в нужный формат
  const transformPayrollData = (data) => {
    if (!data || typeof data !== "object") {
      return null;
    }

    // Если данные уже в правильном формате (есть detailed_12_last_monthes_check_payroll)
    if (data.detailed_12_last_monthes_check_payroll) {
      // Конвертируем английские месяцы в русские в уже готовых данных
      const convertedData = {
        ...data,
        detailed_12_last_monthes_check_payroll:
          data.detailed_12_last_monthes_check_payroll.map((row) => {
            if (row.index && row.index !== "ВСЕГО") {
              const russianDate = convertDateToRussian(row.index);
              return {
                ...row,
                index: russianDate,
              };
            }
            return row;
          }),
      };

      return convertedData;
    }

    // Трансформируем данные из detailed_12_last_monthes_payroll в detailed_12_last_monthes_check_payroll
    const transformed = {
      ...data,
      detailed_12_last_monthes_check_payroll: [],
    };

    if (data.detailed_12_last_monthes_payroll) {
      // Собираем все месяцы из данных
      const months = new Set();
      data.detailed_12_last_monthes_payroll.forEach((record) => {
        Object.keys(record).forEach((key) => {
          if (key !== "Вид выплат" && key !== "ВСЕГО") {
            months.add(key);
          }
        });
      });

      // Для каждого месяца создаем запись
      months.forEach((month) => {
        const monthRecord = { index: convertDateToRussian(month) }; // Конвертируем в русский

        data.detailed_12_last_monthes_payroll.forEach((record) => {
          const paymentType = record["Вид выплат"];
          const amount = record[month] || 0;

          switch (paymentType) {
            case "Зарплата":
              monthRecord["Зарплата"] = amount;
              break;
            case "Бонусы":
              monthRecord["Бонусы"] = amount;
              break;
            case "Субподряд":
              monthRecord["Субподряд"] = amount;
              break;
            case "Отпускные":
              monthRecord["Отпускные"] = amount;
              break;
            case "Больничный":
              monthRecord["Больничный"] = amount;
              break;
            case "ВСЕГО":
              monthRecord["ИТОГО"] = amount;
              break;
          }
        });

        // Вычисляем дополнительные поля
        monthRecord["ИТОГО без субподряда"] =
          (monthRecord["Зарплата"] || 0) +
          (monthRecord["Отпускные"] || 0) +
          (monthRecord["Больничный"] || 0) +
          (monthRecord["Бонусы"] || 0);

        // Если ИТОГО не установлено, вычисляем его
        if (!monthRecord["ИТОГО"]) {
          monthRecord["ИТОГО"] =
            monthRecord["ИТОГО без субподряда"] +
            (monthRecord["Субподряд"] || 0);
        }

        transformed.detailed_12_last_monthes_check_payroll.push(monthRecord);
      });
    }

    return transformed;
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setIsLoadingEmployees(true);
        // Получаем всех пользователей одним запросом
        const response = await getUsersSummary({ page: 1, pageSize: 1000 });
        const users = response.users || [];

        console.log("Loaded employees:", {
          totalUsers: users.length,
          responseStructure: response,
        });
        const formattedUsers = users.map((user) => ({
          value: user.id,
          label: `${user.surname || user.lastName || ""} ${
            user.name || user.firstName || ""
          } ${user.patronymic || user.middleName || ""}`.trim(),
          userData: user,
        }));
        setEmployees(formattedUsers);

        // Устанавливаем текущего пользователя как выбранного по умолчанию, только если ещё не выбран
        const currentUser = formattedUsers.find(
          (user) => user.value === userData?.id
        );
        if (!selectedEmployee && currentUser) {
          console.log("Setting default selected employee:", currentUser);
          setSelectedEmployee(currentUser);
        }
      } catch (error) {
        console.error("Ошибка при загрузке списка сотрудников:", error);
        setError("Не удалось загрузить список сотрудников");
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [userData]);

  useEffect(() => {
    const fetchFullData = async () => {
      if (selectedEmployee) {
        try {
          const response = await getUserById(selectedEmployee.value);
          // Проверяем новую структуру ответа
          const userData = response.users?.[0] || response || null;
          setSelectedEmployeeFullData(userData);
        } catch (e) {
          console.error("Ошибка при получении полных данных пользователя:", e);
          setSelectedEmployeeFullData(null);
        }
      } else {
        setSelectedEmployeeFullData(null);
      }
    };
    fetchFullData();
  }, [selectedEmployee?.value]);

  // Автоматически переключаем с месяца на год для обычных пользователей
  useEffect(() => {
    if (!isAdmin && selectedPeriod === "month") {
      setSelectedPeriod("year");
    }
  }, [isAdmin, selectedPeriod]);

  const displayUser = useMemo(() => {
    return selectedEmployeeFullData || userData;
  }, [selectedEmployeeFullData, userData]);

  const salary = useMemo(
    () => ({
      amount: displayUser?.salary || 0,
      date: new Date().toLocaleDateString("ru-RU", {
        month: "long",
        year: "numeric",
      }),
    }),
    [displayUser?.salary]
  );

  const formatNumber = useCallback((num) => {
    if (num === 0 || num === undefined || num === null) return "-";
    return num.toLocaleString("ru-RU") + " тг";
  }, []);

  const formatPercentage = useCallback((value) => {
    if (value === 0 || value === undefined || value === null) return "-";
    return value + "%";
  }, []);

  const handlePeriodChange = useCallback((period) => {
    setSelectedPeriod(period);
    const currentDate = new Date();

    switch (period) {
      case "year":
        setSelectedYear(currentDate.getFullYear());
        break;
      case "quarter":
        setSelectedQuarter(Math.floor(currentDate.getMonth() / 3) + 1);
        break;
      case "month":
        setSelectedMonth(currentDate);
        break;
      case "period":
        setDateRange({
          startDate: null,
          endDate: null,
        });
        break;
      default:
        break;
    }
  }, []);

  const getQuarterLabel = useCallback((quarter) => {
    const months = {
      1: "январь, февраль, март",
      2: "апрель, май, июнь",
      3: "июль, август, сентябрь",
      4: "октябрь, ноябрь, декабрь",
    };
    return `Q${quarter} (${months[quarter]})`;
  }, []);

  const renderPeriodSelector = useCallback(() => {
    switch (selectedPeriod) {
      case "year":
        return (
          <div className="new-payslip__container-table-year">
            <button
              className="new-payslip__container-table-year-btn"
              onClick={() => setSelectedYear((prev) => prev - 1)}
              disabled={selectedYear <= MIN_YEAR}
            >
              &lt;
            </button>
            <span>{selectedYear}</span>
            <button
              className="new-payslip__container-table-year-btn"
              onClick={() => setSelectedYear((prev) => prev + 1)}
              disabled={selectedYear >= MAX_YEAR}
            >
              &gt;
            </button>
          </div>
        );
      case "quarter":
        return (
          <div className="new-payslip__container-table-quarter">
            <button
              className="new-payslip__container-table-year-btn"
              onClick={() => setSelectedYear((prev) => prev - 1)}
              disabled={selectedYear <= MIN_YEAR}
            >
              &lt;
            </button>
            <span>{selectedYear}</span>
            <button
              className="new-payslip__container-table-year-btn"
              onClick={() => setSelectedYear((prev) => prev + 1)}
              disabled={selectedYear >= MAX_YEAR}
            >
              &gt;
            </button>
            <button
              className="new-payslip__container-table-year-btn"
              onClick={() =>
                setSelectedQuarter((prev) => Math.max(1, prev - 1))
              }
              disabled={selectedQuarter === 1}
            >
              &lt;
            </button>
            <span>{getQuarterLabel(selectedQuarter)}</span>
            <button
              className="new-payslip__container-table-year-btn"
              onClick={() =>
                setSelectedQuarter((prev) => Math.min(4, prev + 1))
              }
              disabled={selectedQuarter === 4}
            >
              &gt;
            </button>
          </div>
        );
      case "month":
        return (
          <div
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                background: "#fff",
                borderRadius: 8,
                padding: "8px 24px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                position: "relative",
              }}
            >
              <button
                className="new-payslip__container-table-year-btn"
                onClick={() =>
                  setSelectedMonth((prev) => {
                    const d = new Date(prev);
                    d.setMonth(d.getMonth() - 1);
                    return d;
                  })
                }
                disabled={
                  selectedMonth.getFullYear() <= MIN_YEAR &&
                  selectedMonth.getMonth() === 0
                }
              >
                &lt;
              </button>
              <DatePicker
                selected={selectedMonth}
                onChange={(date) => {
                  setSelectedMonth(date);
                  setIsMonthPickerOpen(false);
                }}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                locale="ru"
                className="new-payslip__datepicker"
                popperPlacement="bottom"
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                customInput={
                  <button
                    type="button"
                    className="new-payslip__container-table-month-label"
                    style={{
                      cursor: "pointer",
                      minWidth: 100,
                      textAlign: "center",
                      background: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: 6,
                      padding: "6px 10px",
                      fontSize: 15,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {monthNamesRuNom[selectedMonth.getMonth()]}{" "}
                    {selectedMonth.getFullYear()}
                  </button>
                }
              />
              <button
                className="new-payslip__container-table-year-btn"
                onClick={() =>
                  setSelectedMonth((prev) => {
                    const d = new Date(prev);
                    d.setMonth(d.getMonth() + 1);
                    return d;
                  })
                }
                disabled={
                  selectedMonth.getFullYear() >= MAX_YEAR &&
                  selectedMonth.getMonth() === MAX_DATE.getMonth()
                }
              >
                &gt;
              </button>
            </div>
          </div>
        );
      case "period":
        return (
          <div
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 32,
                minWidth: 420,
                maxWidth: 520,
                justifyContent: "center",
              }}
            >
              <DatePicker
                selected={dateRange.startDate}
                onChange={(date) =>
                  setDateRange({ ...dateRange, startDate: date })
                }
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                locale="ru"
                className="new-payslip__datepicker"
                popperPlacement="bottom"
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                customInput={
                  <button
                    type="button"
                    className="new-payslip__container-table-month-label"
                    style={{
                      cursor: "pointer",
                      minWidth: 100,
                      textAlign: "center",
                      background: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: 6,
                      padding: "6px 10px",
                      fontSize: 15,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dateRange.startDate
                      ? `${
                          monthNamesRuNom[dateRange.startDate.getMonth()]
                        } ${dateRange.startDate.getFullYear()}`
                      : "Начало периода"}
                  </button>
                }
              />
              <span style={{ fontSize: 32, color: "#888" }}>—</span>
              <DatePicker
                selected={dateRange.endDate}
                onChange={(date) =>
                  setDateRange({ ...dateRange, endDate: date })
                }
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                locale="ru"
                className="new-payslip__datepicker"
                popperPlacement="bottom"
                minDate={dateRange.startDate || MIN_DATE}
                maxDate={MAX_DATE}
                customInput={
                  <button
                    type="button"
                    className="new-payslip__container-table-month-label"
                    style={{
                      cursor: "pointer",
                      minWidth: 100,
                      textAlign: "center",
                      background: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: 6,
                      padding: "6px 10px",
                      fontSize: 15,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dateRange.endDate
                      ? `${
                          monthNamesRuNom[dateRange.endDate.getMonth()]
                        } ${dateRange.endDate.getFullYear()}`
                      : "Конец периода"}
                  </button>
                }
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  }, [
    selectedPeriod,
    selectedYear,
    selectedQuarter,
    selectedMonth,
    dateRange,
    getQuarterLabel,
  ]);

  // Функция для фильтрации данных по выбранному периоду
  const getFilteredPayrollData = useCallback(() => {
    if (!payrollData?.detailed_12_last_monthes_check_payroll) return [];

    const data = payrollData.detailed_12_last_monthes_check_payroll.filter(
      (row) => row.index !== "ВСЕГО"
    );

    switch (selectedPeriod) {
      case "year":
        return data.filter((row) => {
          const [month, year] = row.index.split("-");
          return parseInt(year) === selectedYear;
        });
      case "quarter": {
        const quarterMonths = {
          1: ["Янв", "Фев", "Мар"],
          2: ["Апр", "Май", "Июн"],
          3: ["Июл", "Авг", "Сен"],
          4: ["Окт", "Ноя", "Дек"],
        };
        return data.filter((row) => {
          const [month, year] = row.index.split("-");
          return (
            parseInt(year) === selectedYear &&
            quarterMonths[selectedQuarter].includes(month)
          );
        });
      }
      case "month": {
        const monthStr = selectedMonth.toLocaleString("en-US", {
          month: "short",
        });
        const russianMonthStr = convertMonthToRussian(monthStr);
        const year = selectedMonth.getFullYear();
        return data.filter((row) => {
          const [month, rowYear] = row.index.split("-");
          return parseInt(rowYear) === year && month === russianMonthStr;
        });
      }
      case "period": {
        if (!dateRange.startDate || !dateRange.endDate) return data;
        return data.filter((row) => {
          const [month, year] = row.index.split("-");
          const englishMonth = convertMonthToEnglish(month);
          const rowDate = new Date(
            parseInt(year),
            [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ].indexOf(englishMonth),
            1
          );
          return rowDate >= dateRange.startDate && rowDate <= dateRange.endDate;
        });
      }
      default:
        return data;
    }
  }, [
    payrollData,
    selectedPeriod,
    selectedYear,
    selectedQuarter,
    selectedMonth,
    dateRange,
  ]);

  // Получить последние 12 месяцев для графика
  const getLast12MonthsData = useCallback(() => {
    if (!payrollData?.detailed_12_last_monthes_check_payroll) return [];
    // Исключаем строку "ВСЕГО"
    const data = payrollData.detailed_12_last_monthes_check_payroll.filter(
      (row) => row.index !== "ВСЕГО"
    );
    // Сортируем по дате (index в формате 'Мар-YYYY')
    const monthOrder = [
      "Янв",
      "Фев",
      "Мар",
      "Апр",
      "Май",
      "Июн",
      "Июл",
      "Авг",
      "Сен",
      "Окт",
      "Ноя",
      "Дек",
    ];
    const sorted = [...data].sort((a, b) => {
      const [ma, ya] = a.index.split("-");
      const [mb, yb] = b.index.split("-");
      const da = new Date(parseInt(ya), monthOrder.indexOf(ma), 1);
      const db = new Date(parseInt(yb), monthOrder.indexOf(mb), 1);
      return da - db;
    });
    return sorted.slice(-12);
  }, [payrollData]);

  // Средние общие выплаты: из итоговой строки за 12 мес. (итого Оклад + итого Бонусы) / 12
  const averagePayments = useMemo(() => {
    const last12 = getLast12MonthsData();
    if (!last12.length) return { amount: 0, period: 12 };
    const totalSalary = last12.reduce((sum, row) => sum + (row["Зарплата"] || 0), 0);
    const totalBonuses = last12.reduce((sum, row) => sum + (row["Бонусы"] || 0), 0);
    const total = totalSalary + totalBonuses;
    return {
      amount: total ? Math.round(total / 12) : 0,
      period: 12,
    };
  }, [getLast12MonthsData]);

  // Функция для подсчёта итогов по столбцам для таблицы месяца
  const getMonthTotals = useCallback((data) => {
    const fields = [
      "Зарплата",
      "Отпускные",
      "Больничный",
      "Замещение",
      "Бонусы",
      "ИТОГО без субподряда",
      "Субподряд",
      "ИТОГО с субподрядом",
      "ИТОГО",
    ];
    const totals = {};
    fields.forEach((field) => {
      totals[field] = data.reduce((sum, row) => sum + (row[field] || 0), 0);
    });
    return totals;
  }, []);

  return (
    <div className="new-payslip">
      <div className="new-payslip__header">
        <h2 className="new-payslip__title">Расчетный лист</h2>
        {forceShowSelector && (
          <div className="new-payslip__employee-selector">
            <Select
              value={selectedEmployee}
              onChange={(newEmployee) => {
                console.log("Employee selection changed:", {
                  from: selectedEmployee?.value,
                  to: newEmployee?.value,
                  newEmployee,
                });
                setSelectedEmployee(newEmployee);
              }}
              options={employees}
              placeholder="Выберите сотрудника"
              className="new-payslip__select"
              isLoading={isLoadingEmployees}
              isClearable={false}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <Preloader />
      ) : error ? (
        <div className="new-payslip__error">{error}</div>
      ) : (
        <>
          {/* Информация о пользователе показывается всегда */}
          <ul className="new-payslip__container">
            <li className="new-payslip__container-user">
              <div className="new-payslip__container-user-info">
                <div className="new-payslip__container-user-left">
                  <img
                    src={displayUser?.image || catPhoto}
                    alt="Фото пользователя"
                    className="new-payslip__container-user-avatar"
                  />
                  <p className="new-payslip__container-user-info-name">
                    {displayUser?.surname || displayUser?.lastName}{" "}
                    {displayUser?.name || displayUser?.firstName}{" "}
                    {displayUser?.patronymic || displayUser?.middleName}
                  </p>
                </div>
                <div className="new-payslip__container-user-right">
                  <p className="new-payslip__container-user-info-position">
                    <img src={briefcaseIcon} alt="Должность" />
                    <span>Должность:</span>
                    {displayUser?.departments?.[0]?.groups?.[0]?.roles?.[0]
                      ?.name ||
                      displayUser?.groups?.[0]?.roles?.[0]?.name ||
                      "Не указана"}
                  </p>
                  <p className="new-payslip__container-user-info-department">
                    <img src={bankIcon} alt="Отдел" />
                    <span>Отдел:</span>
                    {displayUser?.departments?.[0]?.groups?.[0]?.name ||
                      displayUser?.groups?.[0]?.name ||
                      "Не указан"}
                  </p>
                  {/* <p className="new-payslip__container-user-info-experience">
                    <img src={alarmIcon} alt="Стаж" />
                    <span>Стаж:</span>
                    {displayUser?.hire_date
                      ? `${Math.floor(
                          (new Date() - new Date(displayUser.hire_date)) /
                            (1000 * 60 * 60 * 24 * 365)
                        )} года`
                      : "Не указан"}
                  </p> */}
                </div>
              </div>
            </li>
            <li className="new-payslip__container-salary">
              <div className="new-payslip__container-salary-item">
                <p className="new-payslip__container-salary-item-title">
                  Оклад
                </p>
                <p className="new-payslip__container-salary-item-value">
                  {salary.amount.toLocaleString("ru-RU")} тг
                </p>
                <p className="new-payslip__container-salary-item-caption">
                  c {salary.date}
                </p>
              </div>
            </li>

            {/* Финансовая информация показывается только при наличии данных */}
            {payrollData ? (
              <>
                <li className="new-payslip__container-total">
                  <div className="new-payslip__container-salary-item">
                    <p className="new-payslip__container-total-title">
                      Средние общие выплаты на последние{" "}
                      {averagePayments.period} мес.
                    </p>
                    <p className="new-payslip__container-total-value">
                      {averagePayments.amount.toLocaleString("ru-RU")} тг
                    </p>
                  </div>
                </li>
                <li className="new-payslip__container-stats">
                  <h3>График динамики общих выплат</h3>
                  <div className="new-payslip__container-stats-chart">
                    <Line
                      data={{
                        labels:
                          getLast12MonthsData().map((row) => row.index) || [],
                        datasets: [
                          {
                            label: "Общие выплаты",
                            data:
                              getLast12MonthsData().map(
                                (row) => row["ИТОГО"]
                              ) || [],
                            borderColor: "rgb(75, 192, 192)",
                            tension: 0.4,
                            fill: true,
                            backgroundColor: "rgba(75, 192, 192, 0.2)",
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          title: {
                            display: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: function (value) {
                                return value.toLocaleString("ru-RU");
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </li>
                <li className="new-payslip__container-donut">
                  <h3>Сводная по выплатам за 12 мес.</h3>
                  <div className="new-payslip__container-donut-content">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "200px",
                        fontSize: "18px",
                        color: "#999",
                        fontWeight: "500",
                      }}
                    >
                      В разработке
                    </div>
                  </div>
                </li>
              </>
            ) : (
              <li className="new-payslip__container-total new-payslip__container-total--unavailable">
                <div className="new-payslip__container-salary-item">
                  <p className="new-payslip__container-total-title">
                    Финансовые данные
                  </p>
                  <p
                    className="new-payslip__container-total-value"
                    style={{ fontSize: "16px", color: "#999" }}
                  >
                    Данные недоступны
                  </p>
                </div>
              </li>
            )}
          </ul>

          {/* Таблица показывается только при наличии данных */}
          {payrollData && (
            <div className="new-payslip__container-table">
              <div className="new-payslip__container-table-header">
                <p className="new-payslip__container-table-header-title">
                  Детализация выплат за выбранный период
                </p>
              </div>
              <div className="new-payslip__container-table-content">
                <div className="new-payslip__container-table-controls">
                  <div className="new-payslip__container-table-periods">
                    <button
                      className={`new-payslip__container-table-period-btn ${
                        selectedPeriod === "year" ? "active" : ""
                      }`}
                      onClick={() => handlePeriodChange("year")}
                    >
                      Год
                    </button>
                    <button
                      className={`new-payslip__container-table-period-btn ${
                        selectedPeriod === "quarter" ? "active" : ""
                      }`}
                      onClick={() => handlePeriodChange("quarter")}
                    >
                      Квартал
                    </button>
                    {isAdmin && (
                      <button
                        className={`new-payslip__container-table-period-btn ${
                          selectedPeriod === "month" ? "active" : ""
                        }`}
                        onClick={() => handlePeriodChange("month")}
                      >
                        Месяц
                      </button>
                    )}
                    <button
                      className={`new-payslip__container-table-period-btn ${
                        selectedPeriod === "period" ? "active" : ""
                      }`}
                      onClick={() => handlePeriodChange("period")}
                    >
                      Период
                    </button>
                  </div>
                  {renderPeriodSelector()}
                </div>
                <table className="new-payslip__table">
                  <thead>
                    <tr>
                      <th>Месяц</th>
                      <th>Оклад</th>
                      <th>Отпускные</th>
                      <th>Больничный</th>
                      <th>Бонусы</th>
                      <th>ИТОГО без субподряда</th>
                      <th>Субподряд</th>
                      <th>ИТОГО с субподрядом</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPeriod === "month" &&
                    payrollData?.detailed_last_month_payroll ? (
                      <>
                        {payrollData.detailed_last_month_payroll.map(
                          (row, idx) => (
                            <tr key={idx}>
                              <td>{row["Наименование проекта"]}</td>
                              <td>{formatNumber(row["Зарплата"])}</td>
                              <td>{formatNumber(row["Отпускные"])}</td>
                              <td>{formatNumber(row["Больничный"])}</td>
                              <td>{formatNumber(row["Замещение"])}</td>
                              <td>{formatNumber(row["Бонусы"])}</td>
                              <td>
                                {formatNumber(row["ИТОГО без субподряда"])}
                              </td>
                              <td>{formatNumber(row["Субподряд"])}</td>
                              <td>
                                {formatNumber(
                                  row["ИТОГО"] || row["ИТОГО с субподрядом"]
                                )}
                              </td>
                            </tr>
                          )
                        )}
                        <tr className="total-row">
                          <td>ВСЕГО</td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["Зарплата"]
                            )}{" "}
                          </td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["Отпускные"]
                            )}{" "}
                          </td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["Больничный"]
                            )}{" "}
                          </td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["Замещение"]
                            )}{" "}
                          </td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["Бонусы"]
                            )}{" "}
                          </td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["ИТОГО без субподряда"]
                            )}{" "}
                          </td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["Субподряд"]
                            )}{" "}
                          </td>
                          <td>
                            {formatNumber(
                              getMonthTotals(
                                payrollData.detailed_last_month_payroll
                              )["ИТОГО"] ||
                                getMonthTotals(
                                  payrollData.detailed_last_month_payroll
                                )["ИТОГО с субподрядом"]
                            )}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <>
                        {getFilteredPayrollData().map((row, index) => (
                          <tr key={index}>
                            <td>{row.index}</td>
                            <td>{formatNumber(row["Зарплата"])}</td>
                            <td>{formatNumber(row["Отпускные"])}</td>
                            <td>{formatNumber(row["Больничный"])}</td>
                            <td>{formatNumber(row["Бонусы"])}</td>
                            <td>
                              {formatNumber(row["ИТОГО без субподряда"])}{" "}
                            </td>
                            <td>{formatNumber(row["Субподряд"])} </td>
                            <td>{formatNumber(row["ИТОГО"])} </td>
                          </tr>
                        ))}
                        {getFilteredPayrollData().length > 0 && (
                          <tr className="total-row">
                            <td>ВСЕГО</td>
                            <td>
                              {formatNumber(
                                getFilteredPayrollData().reduce(
                                  (sum, row) => sum + (row["Зарплата"] || 0),
                                  0
                                )
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                getFilteredPayrollData().reduce(
                                  (sum, row) => sum + (row["Отпускные"] || 0),
                                  0
                                )
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                getFilteredPayrollData().reduce(
                                  (sum, row) => sum + (row["Больничный"] || 0),
                                  0
                                )
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                getFilteredPayrollData().reduce(
                                  (sum, row) => sum + (row["Бонусы"] || 0),
                                  0
                                )
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                getFilteredPayrollData().reduce(
                                  (sum, row) =>
                                    sum + (row["ИТОГО без субподряда"] || 0),
                                  0
                                )
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                getFilteredPayrollData().reduce(
                                  (sum, row) => sum + (row["Субподряд"] || 0),
                                  0
                                )
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                getFilteredPayrollData().reduce(
                                  (sum, row) => sum + (row["ИТОГО"] || 0),
                                  0
                                )
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default NewPayslip;
